import {
  customer360MetricIdentities,
  customer360MetricIdentitiesFor
} from './customer360MetricIdentifiers';
import { identifiersNamedBy } from './metricIdentifiers';

/**
 * PLA-8828: the catalogue's `Show Confidence Score?` column, and the card gate derived from it.
 *
 * `Calculation & Governance Rules` -> *Confidence display eligibility* governs both:
 *
 *   Yes           calculate/display the metric's Result Confidence, and include it in the
 *                 standard Card Confidence.
 *   No            do not calculate/display it, and exclude it from the aggregation.
 *   Metric Itself the metric's own VALUE is a confidence measure -- so it neither carries a Result
 *                 Confidence of its own (confidence-on-confidence) nor contributes one to its
 *                 card. Four Customer 360 metrics are in this class: C-COM-001, C-HYB-001,
 *                 C-HYB-007 and the Phase-2 C-HYB-010.
 *
 * and the rule that decides a CARD: *"If a card has no applicable Yes metrics, do not calculate
 * a standard Card Confidence solely from No/Metric Itself metrics."*
 *
 * Transcribed from the `Metric List` sheet of `Perx 3.0 - Layer 2 Phase 1 Metrics Catalogue.xlsx`
 * (all 101 `Screen = Customer 360` rows, grouped by that sheet's own Card Name so a row can be
 * checked against it), never inferred from the payload: a card's `confidence` block arriving on the
 * wire says the backend computed something, not that the catalogue lets this card show it.
 */
export type ConfidenceDisplayRule = 'yes' | 'no' | 'metricItself';

export const CUSTOMER360_CONFIDENCE_DISPLAY: Record<string, ConfidenceDisplayRule> = {
  // Customer Behaviour Profile
  'C-COM-005': 'yes',
  'C-COM-006': 'yes',
  'C-COM-007': 'yes',

  // Engagement Timeline
  'C-COM-008': 'no',
  'C-COM-009': 'no',
  'C-COM-010': 'no',

  // Sentiment & Expectations
  'C-COM-015': 'yes',
  'C-COM-016': 'yes',
  'C-REV-006': 'no',

  // Customer Details
  'C-COM-024': 'no',

  // Holdings & Customer Value
  'C-COM-003': 'yes',
  'C-COM-004': 'yes',
  'C-COM-023': 'no',

  // Profile Summary
  'C-COM-001': 'metricItself',
  'C-COM-002': 'no',
  'C-COM-025': 'no',

  // Contact Restrictions
  'C-COM-021': 'no',

  // Customer Opportunities & Product Interest
  'C-COM-018': 'no',
  'C-COM-019': 'yes',

  // Ready for Engagement
  'C-COM-020': 'yes',

  // Rewards & Loyalty Activity
  'C-COM-011': 'no',
  'C-COM-012': 'no',
  'C-COM-026': 'no',
  'C-COM-013': 'no',
  'C-COM-014': 'no',
  'C-REV-001': 'no',
  'C-REV-002': 'no',
  'C-REV-003': 'no',
  'C-REV-004': 'no',

  // Data Confidence & Supporting Evidence
  'C-COM-022': 'no',

  // Risk & Unusual Activity
  'C-COM-017': 'no',
  'C-REV-005': 'no',

  // Financial Health
  'C-BFSI-001': 'yes',
  'C-BFSI-002': 'no',
  'C-BFSI-003': 'no',
  'C-BFSI-004': 'no',
  'C-BFSI-005': 'no',

  // Product Readiness
  'C-BFS-PR-001': 'yes',
  'C-BFS-PR-002': 'yes',
  'C-BFS-PR-003': 'yes',
  'C-BFS-PR-004': 'yes',
  'C-BFS-PR-005': 'yes',
  'C-BFS-PR-006': 'yes',
  'C-BFS-PR-007': 'yes',
  'C-BFS-PR-008': 'yes',
  'C-BFS-PR-009': 'yes',
  'C-FIN-PR-001': 'yes',
  'C-FIN-PR-002': 'yes',
  'C-FIN-PR-003': 'yes',
  'C-FIN-PR-004': 'yes',
  'C-FIN-PR-005': 'yes',
  'C-FIN-PR-006': 'yes',
  'C-FIN-PR-007': 'yes',
  'C-FIN-PR-008': 'yes',
  'C-FIN-PR-009': 'yes',
  'C-INS-PR-001': 'yes',
  'C-INS-PR-002': 'yes',
  'C-INS-PR-003': 'yes',
  'C-INS-PR-004': 'yes',
  'C-INS-PR-005': 'yes',
  'C-INS-PR-006': 'yes',
  'C-INS-PR-007': 'yes',
  'C-INS-PR-008': 'yes',
  'C-INS-PR-009': 'yes',
  'C-INS-PR-010': 'yes',
  'C-TEL-PR-001': 'yes',
  'C-TEL-PR-002': 'yes',
  'C-TEL-PR-003': 'yes',
  'C-TEL-PR-004': 'yes',
  'C-TEL-PR-005': 'yes',
  'C-TEL-PR-006': 'yes',
  'C-TEL-PR-007': 'yes',
  'C-TEL-PR-008': 'yes',

  // Cash Usage & Dependency
  'C-HYB-005': 'yes',
  'C-HYB-010': 'metricItself',

  // Payment & Merchant Behaviour
  'C-HYB-004': 'no',
  'C-HYB-006': 'yes',
  'C-HYB-007': 'metricItself',
  'C-HYB-008': 'yes',
  'C-HYB-009': 'no',
  'C-HYB-011': 'no',
  'C-HYB-012': 'yes',

  // Transaction Data Quality
  'C-HYB-001': 'metricItself',
  'C-HYB-002': 'yes',
  'C-HYB-003': 'yes',

  // Payment Usage & Risk
  'C-FIN-003': 'no',
  'C-FIN-005': 'no',

  // Wallet Activity & Adoption
  'C-FIN-001': 'no',
  'C-FIN-002': 'no',
  'C-FIN-004': 'yes',

  // Policy Health & Claims
  'C-INS-001': 'no',
  'C-INS-002': 'no',
  'C-INS-004': 'no',

  // Renewal & Coverage Opportunities
  'C-INS-003': 'yes',
  'C-INS-005': 'yes',
  'C-INS-006': 'yes',

  // Churn & Service Experience
  'C-TEL-004': 'yes',
  'C-TEL-005': 'yes',

  // Plan Usage & Upgrade Readiness
  'C-TEL-001': 'yes',
  'C-TEL-002': 'yes',
  'C-TEL-003': 'yes'
};

/** The catalogue's rule for one metric, or `undefined` for an identifier it does not carry. */
export const confidenceDisplayRuleFor = (identifier: string): ConfidenceDisplayRule | undefined =>
  Object.prototype.hasOwnProperty.call(CUSTOMER360_CONFIDENCE_DISPLAY, identifier)
    ? CUSTOMER360_CONFIDENCE_DISPLAY[identifier]
    : undefined;

// `yes` only. `metricItself` is excluded for a DIFFERENT reason than `no` -- the reading is
// already a confidence -- and an unknown identifier is excluded too, because a card may not be
// scored on the strength of a metric whose eligibility this file cannot state.
const contributesToCardConfidence = (identifier: string): boolean =>
  confidenceDisplayRuleFor(identifier) === 'yes';

const showsConfidenceByCard: Record<string, boolean> = Object.keys(
  customer360MetricIdentities
).reduce((byKey, cardKey) => {
  byKey[cardKey] = customer360MetricIdentitiesFor(cardKey)
    .flatMap(entry => identifiersNamedBy(entry.source))
    .some(contributesToCardConfidence);

  return byKey;
}, {} as Record<string, boolean>);

/**
 * Whether this card may draw a Card Confidence pill at all.
 *
 * Derived from the card's OWN declared metrics rather than hand-passed per section, which is what
 * it was until PLA-8828: seven of the nine sections passed `showsConfidence` as a bare literal, and
 * seven Customer 360 cards whose catalogue metrics are all `No`/`Metric Itself` would have drawn a
 * pill the moment the backend started sending a card score.
 *
 * `identifiersNamedBy` rather than the `metric` kind alone, so a `ratio`/`difference`/`derived`
 * tile is judged on the catalogue metrics it actually reads. An `unmapped` tile names none, and
 * therefore makes no card eligible.
 */
export const customer360CardShowsConfidence = (cardKey: string): boolean =>
  showsConfidenceByCard[cardKey] === true;
